<!-- 
화면명 : UCGrid.vue
작성일 : 2025-07-01
설명   : 그리드 컨트롤
-->
<template>
  <div
    ref="tableRef"
    class="overflow-x-auto w-full"
    :style="{ height: gridHeight + 'px' }"
  />
</template>

<script setup>
import {
  onMounted,
  onUnmounted,
  onActivated,
  ref,
  watch,
  onBeforeUnmount,
  defineProps,
  defineEmits,
  defineExpose,
  nextTick,
} from "vue";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator_site.min.css";

// Props
const props = defineProps({
  data: { type: Array, default: () => [] },
  columns: { type: Array, required: true },
  options: { type: Object, default: () => ({}) },
  selectable: { type: Boolean, default: false },
  pagination: { type: Boolean, default: true },
  paginationSize: { type: Number, default: 10 },
  isRowNumber: { type: Boolean, default: false },
  autoHeight: { type: Boolean, default: false },
  heightOffset: { type: Number, default: 30 }, // 하단 여백 padding 값
  excludeSelectors: { type: Array, default: () => [] }, // 높이 제외할 요소 (호환성 유지)
});

// Emits
const emit = defineEmits(["row-selected", "table-ready"]);

const tableRef = ref(null);
let tabulator = null;

onMounted(() => {
  // console.log("onMounted UCgGrid.vue");

  calcGridHeight();
  window.addEventListener("resize", calcGridHeight);

  // row번호 표시 옵션
  if (props.isRowNumber) {
    props.columns.unshift({
      title: "No.",
      //formatter: "rownum",
      hozAlign: "center",
      width: 75,
      formatter: function (cell) {
        //return cell.getRow().getPosition(true) + 1 - 1;
        // true → 현재 전체 데이터에서의 절대 위치

        const table = cell.getTable();
        const page = table.getPage(); // 현재 페이지 정보
        const pageSize = table.getPageSize(); // 현재 페이지 정보
        const rowPosition = cell.getRow().getPosition(false); // 현재 페이지 내 위치(0부터 시작)

        return (page - 1) * pageSize + (rowPosition + 1 - 1);
      },
    });
  }

  tabulator = new Tabulator(tableRef.value, {
    data: props.data,
    columns: props.columns,
    height: props.autoHeight ? (gridHeight.value ? gridHeight.value + "px" : "100%") : (props.height ? props.height + "px" : undefined),
    layout: "fitColumns",
    // layout: "fitData",
    // layout: "fitDataFill",
    // responsiveLayout: "collapse", // or "collapse"
    columnHeaderVertAlign: "center", // 세로 가운데 정렬
    reactiveData: true,
    selectable: props.selectable,
    pagination: props.pagination,
    paginationSize: props.paginationSize,
    ...props.options,
  });

  if (props.selectable) {
    tabulator.on("rowSelectionChanged", function (data, rows) {
      emit("row-selected", data);
    });
  }

  emit("table-ready", tabulator);

  // 마운트 직후 렌더링 안정화 시점 재계산
  setTimeout(() => {
    calcGridHeight();
  }, 100);
});

onActivated(() => {
  calcGridHeight();
  setTimeout(() => {
    calcGridHeight();
    if (tabulator) {
      tabulator.redraw(true);
    }
  }, 100);
});

onUnmounted(() => {
  // console.log("onUnmounted UCgGrid.vue");

  window.removeEventListener("resize", calcGridHeight);
});

// 언마운트 시 정리
onBeforeUnmount(() => {
  // console.log("onBeforeUnmount UCgGrid.vue");

  if (tabulator) {
    tabulator.destroy();
    tabulator = null;
  }
});

// 데이터 변경 감지 (옵션)
watch(
  () => props.data,
  (newData) => {
    if (tabulator) {
      tabulator.replaceData(newData);

      calcGridHeight();
      setTimeout(calcGridHeight, 50);
    }
  }
);

// Excel 다운로드 함수
function downloadExcel() {
  // console.log("downloadExcel");

  if (tabulator) {
    tabulator.download("xlsx", "export.xlsx", {
      sheetName: "MySheet",
    });
  }
}

// ✅ expose 함수 등록
defineExpose({
  downloadExcel,
});

const gridHeight = ref(props.height || 400); // 기본값 또는 props.height
const calcGridHeight = () => {
  if (!props.autoHeight) return;

  nextTick(() => {
    if (!tableRef.value) return;

    // 그리드 엘리먼트의 화면 상단으로부터의 현재 위치(Y 좌표)
    const rect = tableRef.value.getBoundingClientRect();
    const topOffset = rect.top;

    // 하단 여유 공간 (기본값 20px)
    const bottomOffset = props.heightOffset !== undefined ? props.heightOffset : 20;

    // 화면 전체 높이 - 상단까지의 거리 - 하단 여백 = 그리드가 차지해야 할 정확한 높이
    const calculated = window.innerHeight - topOffset - bottomOffset;

    const finalHeight = Math.max(200, Math.floor(calculated));
    gridHeight.value = finalHeight;

    if (tabulator) {
      tabulator.setHeight(finalHeight);
    }
  });
};
</script>

<style>
.tabulator {
  border: 1px solid #e1e1e1 !important;
}

/* ✅ Tabulator 헤더 스타일 커스터마이징 (다크 테마) */
.tabulator .tabulator-header {
  background: linear-gradient(to bottom, #374151 0%, #1f2937 100%) !important; /* Tailwind: gray-700 to gray-800 */
  border-bottom: 1px solid #111827 !important; /* Tailwind: gray-900 */
}

.tabulator .tabulator-frozen {
  background-color: white; /* 고정 컬럼이 투명하지 않도록 */
  z-index: 10;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05); /* 자연스러운 그림자 */
}

/* 고정 컬럼 헤더 (다크 테마에 맞게 수정) */
.tabulator .tabulator-frozen .tabulator-col {
  background-color: #1f2937 !important; 
  z-index: -1;
}

/* ✅ 헤더 컬럼 기본 (다크 배경 & 흰색 글자) */
.tabulator .tabulator-header .tabulator-col {
  background: linear-gradient(to bottom, #374151 0%, #1f2937 100%) !important;
  color: #ffffff !important; /* 글자를 흰색으로 지정 */
  font-weight: 600;
  font-size: 0.875rem;
  text-align: center;
  border-right: 1px solid #4b5563 !important; /* 다크 테마에 어울리는 컬럼 구분선 */
}

.tabulator .tabulator-header .tabulator-col .tabulator-col-content {
  padding: 0.75rem 0.5rem;
}

/* 헤더 정렬 화살표 */
.tabulator .tabulator-header .tabulator-arrow {
  color: #9ca3af; /* Tailwind: text-gray-400 (어두운 배경에 잘 보이는 밝은 회색) */
}

/* ✅ Tabulator 헤더 셀 hover 시 색상 (조금 더 밝은 다크톤으로) */
.tabulator .tabulator-col:hover {
  background: linear-gradient(to bottom, #4b5563 0%, #374151 100%) !important; /* Tailwind: gray-600 to gray-700 */
  color: #ffffff !important; 
  transition: background 0.2s ease, color 0.2s ease;
  cursor: default;
}

/* =========================================================================
   아래부터는 기존의 Row 데이터 및 페이징 영역 스타일입니다 (그대로 유지)
   ========================================================================= */

/* ✅ 마우스 hover 시 배경 색상 변경 */
.tabulator .tabulator-row:hover {
  background-color: #f1f5f9 !important; /* Tailwind: bg-blueGray-100 */
  cursor: default;
}

/* ✅ 선택된 row 배경 색상 변경 */
.tabulator .tabulator-row.tabulator-selected {
  background-color: #e0e7fe !important; /* Tailwind: bg-lightBlue-100 */
  color: #0c4a6e !important; /* Tailwind: text-lightBlue-800 */
}

/* 특정 컬럼만 손모양 표시 */
.tabulator .tabulator-cell.cursor-pointer {
  cursor: pointer !important;
  text-decoration: underline;
}

/* 헤더는 제외 */
.tabulator .tabulator-col.cursor-pointer {
  cursor: default !important;
}

/* ✅ 페이징 전체 영역 배경 */
.tabulator .tabulator-footer {
  background-color: #f9fafb !important; /* bg-gray-50 */
  border-top: 1px solid #e5e7eb !important; /* border-gray-200 */
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  display: flex;
  justify-content: center; /* 수평 가운데 정렬 */
  align-items: center;
}

/* ✅ 페이지 번호 버튼 */
.tabulator .tabulator-page {
  background-color: #ffffff !important; /* bg-white */
  border: 1px solid #d1d5db !important; /* border-gray-300 */
  color: #1f2937 !important; /* text-gray-800 */
  border-radius: 0.375rem !important; /* rounded-md */
  padding: 0.25rem 0.5rem !important; /* py-1 px-2 */
  margin: 0 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
}

/* ✅ hover 시 효과 */
.tabulator .tabulator-page:hover {
  background-color: #e0e7fe !important; /* bg-lightBlue-100 */
  color: #0c4a6e !important; /* text-lightBlue-800 */
}

/* ✅ 현재 페이지 버튼 강조 */
.tabulator .tabulator-page.active {
  background-color: #3b82f6 !important; /* bg-blue-500 */
  color: #ffffff !important; /* text-white */
  font-weight: bold;
  border-color: transparent !important;
}

/* ✅ 페이지당 개수 드롭다운 (select) */
.tabulator .tabulator-page-size {
  border: 1px solid #d1d5db !important; /* border-gray-300 */
  border-radius: 0.375rem !important; /* rounded-md */
  padding: 0.25rem 0.5rem !important;
  background-color: #fff !important;
  color: #374151 !important; /* text-gray-700 */
  margin-left: 0.5rem;
  width: 70px;
}
</style>
